/**
 * Settings.js — inbox preferences screen.
 *
 * Separate from Profile (which is identity / account / logout). Settings
 * is the agent's preferences hub: quick replies, notifications, etc.
 *
 * Channel colors + labels are NOT here — they live in the end-user
 * dashboard because only the account owner can edit them (per #98).
 * The PWA reads them as account-shared metadata via the realtime
 * channel-meta sync.
 *
 * Updated 2026-04-14 (#93): removed the Phase 2.8 stub rows for
 * features that have since shipped (channel filter #87, templates
 * #63/#68, haptics #78). The Sound row's subtitle now also explains
 * that it controls haptics, so there's no need for a separate toggle.
 */

import { html } from 'htm/preact';
import { useState, useEffect } from 'preact/hooks';
import { useStore } from '@nanostores/preact';

import {
  navigate, showToast,
  $mutedChats, $quietHours, setQuietHours, unmuteChat,
} from '../stores/ui.js';
import { $chats } from '../stores/chats.js';
import { SCREENS, BUILD_VERSION } from '../shared/constants.js';
import { isSoundOn, toggleSound } from '../services/realtime.js';
import { requestPushPermission, $pushPermission, runPushDiagnostic } from '../services/push.js';

// "HH:MM" ↔ minutes-since-midnight helpers, used by the quiet-hours
// time inputs. Native <input type="time"> takes/returns "HH:MM".
function _minToHHMM(min) {
  const h = String(Math.floor(min / 60) % 24).padStart(2, '0');
  const m = String(min % 60).padStart(2, '0');
  return `${h}:${m}`;
}
function _hhmmToMin(hhmm) {
  const [h, m] = String(hhmm || '00:00').split(':').map(n => parseInt(n, 10) || 0);
  return (h * 60 + m) % 1440;
}

/**
 * A single tappable row inside a Settings card. Keeps visual consistency
 * across the section list — icon + label on the left, optional value / arrow
 * on the right, full-width tap target.
 */
function SettingsRow({ icon, label, sub, value, onClick, comingSoon }) {
  return html`
    <button
      class="inbox-settings-row"
      onClick=${onClick}
      style="display:flex;align-items:center;gap:12px;width:100%;padding:14px 14px;background:transparent;border:none;border-top:1px solid rgba(255,255,255,0.06);color:inherit;cursor:pointer;text-align:left"
    >
      <div style="flex-shrink:0;width:36px;height:36px;border-radius:10px;background:rgba(37,211,102,0.12);display:flex;align-items:center;justify-content:center;color:#25D366">
        ${icon}
      </div>
      <div style="flex:1;min-width:0;display:flex;flex-direction:column;gap:2px">
        <span style="font-size:0.9rem;font-weight:600">${label}</span>
        ${sub && html`<span style="font-size:0.72rem;color:var(--text-muted, #7d8799);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${sub}</span>`}
      </div>
      <div style="flex-shrink:0;display:flex;align-items:center;gap:8px">
        ${value && html`<span style="font-size:0.78rem;color:var(--text-muted, #7d8799)">${value}</span>`}
        ${comingSoon && html`
          <span style="font-size:0.65rem;padding:3px 7px;border-radius:8px;background:rgba(245,158,11,0.15);color:#fbbf24;font-weight:600;letter-spacing:0.02em">SOON</span>
        `}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color:var(--text-muted, #7d8799)">
          <path d="m9 18 6-6-6-6"/>
        </svg>
      </div>
    </button>
  `;
}

function SectionHeader({ children }) {
  return html`
    <div style="padding:14px 14px 6px;font-size:0.72rem;text-transform:uppercase;letter-spacing:0.08em;color:var(--text-muted, #7d8799);font-weight:600">
      ${children}
    </div>
  `;
}

function Card({ children }) {
  return html`
    <div style="background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.06);border-radius:14px;overflow:hidden;margin:0 12px 14px">
      ${children}
    </div>
  `;
}

export function Settings() {
  const [soundOn, setSoundOn] = useState(isSoundOn());
  const pushPerm = useStore($pushPermission);
  const [enabling, setEnabling] = useState(false);
  // ── Full Inbox Diagnostic ────────────────────────────────────────
  // Captures EVERYTHING support needs to triage an end-user issue:
  //   - Session identifiers (account, user, agent, role, partner ids)
  //   - Permission audit (gate, missing required, runtime hints)
  //   - Device / browser / PWA-install status
  //   - Push notification state + last error from initPush
  //   - Backend health (chats list reachable? me reachable? config reachable?)
  // One Copy button serializes the whole thing to clipboard for paste-
  // into-chat / paste-into-email. Lives in its own Settings card so a
  // user reporting any inbox issue can capture state in a single tap
  // without screenshots.
  const [diagOpen, setDiagOpen]   = useState(false);
  const [diag, setDiag]           = useState(null);
  const [diagBusy, setDiagBusy]   = useState(false);

  const _probe = async (label, url) => {
    const t0 = Date.now();
    try {
      const r = await fetch(url, { credentials: 'include' });
      return { label, status: r.status, ok: r.ok, ms: Date.now() - t0 };
    } catch (e) {
      return { label, status: 0, ok: false, ms: Date.now() - t0, error: e?.message || 'fetch threw' };
    }
  };

  const runDiag = async () => {
    if (diagBusy) return;
    setDiagBusy(true);
    try {
      // Pull everything in parallel — diag is read-only, no ordering needed.
      const [push, me, permCheck, devicesRes, healthChats, healthRealtime] = await Promise.all([
        runPushDiagnostic().catch(e => ({ error: e?.message })),
        fetch('/.netlify/functions/inbox-auth?action=me',                       { credentials: 'include' }).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch('/.netlify/functions/inbox-proxy?path=/permissions-check',        { credentials: 'include' }).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch('/.netlify/functions/inbox-push?action=devices',                  { credentials: 'include' }).then(r => r.ok ? r.json() : null).catch(() => null),
        _probe('chats',          '/.netlify/functions/inbox-proxy?path=/chats&limit=1'),
        // /realtime-config is an inbox-auth ACTION (not a proxy route).
        // The earlier `inbox-proxy?path=/realtime-config` probe was always
        // 403 because the proxy refuses unknown paths.
        _probe('realtime-config','/.netlify/functions/inbox-auth?action=realtime-config'),
      ]);

      // Browser / viewport / install state
      const ua = navigator.userAgent || '';
      const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
      const isAndroid = /Android/.test(ua);
      let iosVersion = '';
      if (isIOS) {
        const m = ua.match(/OS (\d+)_(\d+)(?:_(\d+))?/);
        if (m) iosVersion = `${m[1]}.${m[2]}${m[3] ? '.' + m[3] : ''}`;
      }
      const isStandalone =
        navigator.standalone === true ||
        (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);

      const session = me || {};
      const perms = Array.isArray(session.permissions) ? session.permissions : [];
      const hasGate = perms.includes('crm.live_chat.ALL');

      setDiag({
        at: new Date(),
        meta: {
          build: BUILD_VERSION,
          url:   location.href.split('?')[0],
        },
        session: {
          sessionId:    session.sessionId,
          accountId:    session.accountId,
          userType:     session.userType,
          agentId:      session.agentId,
          agentName:    session.agentName,
          agentEmail:   session.agentEmail,
          agentRole:    session.agentRole,
          wabaId:       session.wabaId,
          phoneNumberId:session.phoneNumberId,
          channels:     session.channels || [],
          permsCount:   perms.length,
          hasGate,
        },
        permsAudit: permCheck?.data || null,
        device: {
          isIOS, iosVersion, isAndroid,
          isStandalone,
          platform:  navigator.platform,
          lang:      navigator.language,
          online:    navigator.onLine,
          screen:    `${screen.width}x${screen.height} @${window.devicePixelRatio || 1}x`,
          viewport:  `${window.innerWidth}x${window.innerHeight}`,
          ua,
        },
        push:    push && !push.error ? push : { error: push?.error || 'diagnostic failed' },
        devices: devicesRes?.devices || [],
        health:  [healthChats, healthRealtime],
      });
    } finally {
      setDiagBusy(false);
    }
  };

  // Serialize the snapshot to a clipboard-friendly text block. Designed
  // for paste-into-chat or save-as-.txt: groups by section with a
  // single line per fact and dollar-zero alignment so it reads cleanly
  // even in monospace email. Kept as a free function so both copyDiag
  // and downloadDiag produce the exact same content.
  const _buildDiagText = (d) => {
    if (!d) return '';
    const lines = [];
    const push = (s = '') => lines.push(s);

    push('═══ ODIV INBOX DIAGNOSTIC ═══');
    push(`Captured: ${d.at.toLocaleString()}`);
    push(`Build:    ${d.meta.build}`);
    push(`URL:      ${d.meta.url}`);

    push('');
    push('── ACCOUNT ──');
    push(`User type:    ${d.session.userType || '?'}`);
    push(`Account ID:   ${d.session.accountId || '—'}`);
    push(`Agent name:   ${d.session.agentName || '—'}`);
    push(`Agent email:  ${d.session.agentEmail || '—'}`);
    push(`Agent role:   ${d.session.agentRole || '—'}`);
    push(`Agent ID:     ${d.session.agentId || '—'}`);
    push(`Session ID:   ${d.session.sessionId || '—'}`);
    push(`WABA ID:      ${d.session.wabaId || '—'}`);
    push(`Phone num ID: ${d.session.phoneNumberId || '—'}`);
    push(`Channels:     ${d.session.channels.length}`);
    for (const ch of d.session.channels) {
      push(`  · ${ch.display_name || '?'} — channel_id=${ch.channel_id} pnid=${ch.phone_number_id}`);
    }

    push('');
    push('── PERMISSIONS ──');
    push(`Total perms:  ${d.session.permsCount}`);
    push(`Has gate:     ${d.session.hasGate ? 'yes (crm.live_chat.ALL)' : 'NO — gate missing'}`);
    if (d.permsAudit) {
      push(`Audit ok:     ${d.permsAudit.ok ? 'yes' : 'NO'}`);
      push(`Owner bypass: ${d.permsAudit.ownerBypass ? 'yes' : 'no'}`);
      if (d.permsAudit.missing_required?.length) {
        push(`Missing required:`);
        for (const m of d.permsAudit.missing_required) push(`  · ${m.label} (${m.path})`);
      }
    }

    push('');
    push('── DEVICE ──');
    push(`Platform:    ${d.device.platform}${d.device.isIOS ? ` · iOS ${d.device.iosVersion}` : d.device.isAndroid ? ' · Android' : ''}`);
    push(`Installed:   ${d.device.isStandalone ? 'standalone PWA' : 'browser tab'}`);
    push(`Online:      ${d.device.online ? 'yes' : 'NO'}`);
    push(`Language:    ${d.device.lang}`);
    push(`Screen:      ${d.device.screen}`);
    push(`Viewport:    ${d.device.viewport}`);
    push(`User agent:  ${d.device.ua}`);

    push('');
    push('── PUSH NOTIFICATIONS ──');
    const p = d.push || {};
    push(`Permission:        ${p.permission || '?'}`);
    push(`Service worker:    ${p.sw_registered ? (p.sw_state || 'active') : 'NOT REGISTERED'}`);
    push(`SW scope:          ${p.sw_scope || '—'}`);
    push(`Push subscription: ${p.push_subscription_present ? `present (${p.push_subscription_endpoint || ''})` : 'none'}`);
    push(`VAPID key:         ${p.vapid_key_fetched ? 'fetched' : 'MISSING'}`);
    push(`FCM token (local): ${p.fcm_token_present ? p.fcm_token_preview : 'NOT PRESENT'}`);
    push(`Backend devices:   ${p.backend_token_count ?? '?'}`);
    const dev = d.devices?.[0];
    if (dev) {
      push(`Last device:       ${dev.fcm_token_preview}`);
      push(`Last updated:      ${new Date(dev.updated_at).toLocaleString()}`);
      if (dev.device_info?.ua) push(`Last device UA:    ${dev.device_info.ua}`);
    }
    if (p.last_error) {
      push('');
      push('LAST ERROR:');
      push(p.last_error);
    }

    push('');
    push('── HEALTH CHECKS ──');
    for (const h of d.health || []) {
      push(`${h.label.padEnd(16)} → ${h.ok ? 'OK' : 'FAIL'} (HTTP ${h.status}, ${h.ms}ms)${h.error ? ' — ' + h.error : ''}`);
    }

    return lines.join('\n');
  };

  const copyDiag = async () => {
    if (!diag) return;
    const text = _buildDiagText(diag);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      showToast('Diagnostic copied — paste in chat or email', 2400);
    } catch (e) {
      showToast(`Copy failed: ${e?.message || 'unknown'}`, 2800);
    }
  };

  // Serialize + download as a .txt file. Triggers a real file download
  // so the customer can attach it to an email without having to paste
  // a multi-screen-tall snippet into a tiny chat box. Same content as
  // the clipboard variant — kept in lockstep via _buildDiagText.
  const downloadDiag = () => {
    if (!diag) return;
    try {
      const text = _buildDiagText(diag);
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.href = url;
      a.download = `odiv-inbox-diagnostic-${stamp}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      showToast('Diagnostic downloaded', 2000);
    } catch (e) {
      showToast(`Download failed: ${e?.message || 'unknown'}`, 2800);
    }
  };

  const reRegisterPush = async () => {
    if (diagBusy) return;
    setDiagBusy(true);
    showToast('Re-registering for push…', 1600);
    try {
      const tok = await requestPushPermission();
      if (tok) showToast('Push re-registered ✓', 1800);
      else     showToast('Re-register did not complete — Download/Copy diagnostic and share with support', 3800);
    } finally {
      setDiagBusy(false);
      runDiag();
    }
  };

  // Reduce the rich diagnostic snapshot to a small list of customer-
  // safe status rows. We deliberately do NOT surface raw error text or
  // long IDs to the UI — those go into the Copy/Download payload only.
  // Visible UI just signals "this check is OK / has an issue / unknown"
  // so a non-technical user can tell support there's a problem without
  // staring at scary stack traces.
  const _diagStatusRows = (d) => {
    if (!d) return [];
    const out = [];
    const sess = d.session || {};
    const dev  = d.device  || {};
    const p    = d.push    || {};
    const audit = d.permsAudit || {};
    const health = d.health || [];
    const healthOk = health.length > 0 && health.every(h => h.ok);

    out.push({
      label: 'Account',
      ok: !!sess.accountId,
      hint: sess.userType === 'owner' ? 'Account owner' : 'Team member',
    });
    out.push({
      label: 'Permissions',
      ok: sess.userType === 'owner' || audit.ok !== false,
      hint: sess.userType === 'owner'
        ? 'Owner — all perms'
        : audit.ok === false ? 'Some permissions missing' : 'All checks passed',
    });
    out.push({
      label: 'Installed app',
      ok: dev.isStandalone || !dev.isIOS,
      hint: dev.isStandalone ? 'Standalone PWA' : (dev.isIOS ? 'Add to Home Screen for push' : 'Browser'),
    });
    out.push({
      label: 'Service worker',
      ok: !!p.sw_registered,
      hint: p.sw_registered ? (p.sw_state || 'Active') : 'Not registered',
    });
    out.push({
      label: 'Push notifications',
      ok: p.permission === 'granted' && p.fcm_token_present && (p.backend_token_count ?? 0) > 0,
      hint: p.permission !== 'granted' ? 'Permission not granted'
          : !p.fcm_token_present     ? 'Registration incomplete'
          : (p.backend_token_count ?? 0) === 0 ? 'No device on file'
          : 'Ready',
    });
    out.push({
      label: 'Server connection',
      ok: healthOk,
      hint: healthOk ? 'All endpoints reachable' : 'Some endpoints unreachable',
    });
    return out;
  };
  // #180 + #181 — reactive notification preferences.
  const muted   = useStore($mutedChats);
  const quiet   = useStore($quietHours);
  const chats   = useStore($chats);
  const mutedRows = (() => {
    if (!muted.size) return [];
    const out = [];
    const seen = new Set();
    for (const k in chats) {
      const c = chats[k];
      if (c?.contactId && muted.has(String(c.contactId)) && !seen.has(c.contactId)) {
        seen.add(c.contactId);
        out.push({ contactId: c.contactId, name: c.name || c.phone || 'Unknown' });
      }
    }
    // Surface any muted contactIds not currently in the loaded chat list
    // so the agent can still unmute them. Falls back to showing the raw id.
    for (const id of muted) {
      if (!seen.has(id)) out.push({ contactId: id, name: id });
    }
    return out;
  })();
  // S4 — stale-JS comparison removed. SW's activate handler force-navigates
  // open windows on update, so a tab can't legitimately drift from the
  // deployed version. If the SW itself is wedged, agents can hard-refresh.

  const handleToggleSound = () => {
    const next = toggleSound();
    setSoundOn(next);
    showToast(next ? 'Sound + haptics on' : 'Sound + haptics off', 1500);
  };

  const goDashboard = () => {
    // Owner-only customization lives in the end-user dashboard; agents who
    // tap this just learn where it is. Same pattern Profile uses for
    // "Open full dashboard".
    showToast('Channel colors live in your dashboard → Account', 2400);
  };

  return html`
    <div class="inbox-screen inbox-screen-settings" style="display:flex">
      <!-- Header -->
      <div class="inbox-header">
        <button class="inbox-header-icon-btn" onClick=${() => navigate(SCREENS.PROFILE)} title="Back">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="m15 18-6-6 6-6"/>
          </svg>
        </button>
        <h2 class="inbox-header-title">Settings</h2>
      </div>

      <!-- Content (scrollable) -->
      <div style="flex:1;overflow-y:auto;padding:10px 0 24px">

        <${SectionHeader}>Productivity<//>
        <${Card}>
          <${SettingsRow}
            label="Quick replies"
            sub="Canned responses you trigger with /slash in the composer"
            onClick=${() => navigate(SCREENS.QUICK_REPLIES)}
            icon=${html`
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="13 17 18 12 13 7"/>
                <polyline points="6 17 11 12 6 7"/>
              </svg>
            `}
          />
        <//>

        <${SectionHeader}>Notifications<//>
        <${Card}>
          <div style="display:flex;align-items:center;gap:12px;padding:14px;border-top:1px solid rgba(255,255,255,0.06)">
            <div style="flex-shrink:0;width:36px;height:36px;border-radius:10px;background:rgba(37,211,102,0.12);display:flex;align-items:center;justify-content:center;color:#25D366">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
              </svg>
            </div>
            <div style="flex:1;min-width:0;display:flex;flex-direction:column;gap:2px">
              <span style="font-size:0.9rem;font-weight:600">Sound + haptics</span>
              <span style="font-size:0.72rem;color:var(--text-muted, #7d8799)">Chime and vibrate on incoming messages</span>
            </div>
            <div
              onClick=${handleToggleSound}
              style="position:relative;width:46px;height:28px;flex-shrink:0;cursor:pointer;-webkit-tap-highlight-color:transparent"
              role="switch"
              aria-checked=${soundOn}
              title=${soundOn ? 'Tap to mute' : 'Tap to enable'}
            >
              <div style="position:absolute;inset:0;background:${soundOn ? '#25D366' : 'rgba(255,255,255,0.12)'};border-radius:14px;transition:background 0.2s ease"></div>
              <div style="position:absolute;top:3px;left:${soundOn ? '21px' : '3px'};width:22px;height:22px;background:#fff;border-radius:50%;box-shadow:0 1px 3px rgba(0,0,0,0.3);transition:left 0.2s ease"></div>
            </div>
          </div>

          <!-- Push notifications row — toggle behavior only. The full
               diagnostic + Re-register + Copy lives in the dedicated
               Diagnostics card lower on this screen so anyone can
               capture full inbox state in one tap. -->
          <div style="border-top:1px solid rgba(255,255,255,0.06);padding:14px;display:flex;align-items:center;gap:12px">
            <div style="flex-shrink:0;width:36px;height:36px;border-radius:10px;background:${pushPerm === 'denied' ? 'rgba(239,68,68,0.12)' : 'rgba(37,211,102,0.12)'};display:flex;align-items:center;justify-content:center;color:${pushPerm === 'denied' ? '#f87171' : '#25D366'}">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
            </div>
            <div style="flex:1;min-width:0">
              <div style="font-size:0.9rem;font-weight:600">Push notifications</div>
              <div style="font-size:0.72rem;color:var(--text-muted,#7d8799);margin-top:1px;line-height:1.4">
                ${pushPerm === 'granted' ? 'Enabled — alerts when the app is closed'
                  : pushPerm === 'denied' ? 'Blocked by your browser — check OS settings'
                  : 'Get notified even when the app is closed'}
              </div>
            </div>
            ${pushPerm === 'granted' && html`
              <span style="font-size:0.72rem;font-weight:600;color:#25D366;background:rgba(37,211,102,0.12);padding:4px 10px;border-radius:8px">Active</span>
            `}
            ${pushPerm === 'denied' && html`
              <span style="font-size:0.72rem;font-weight:600;color:#f87171;background:rgba(239,68,68,0.1);padding:4px 10px;border-radius:8px">Blocked</span>
            `}
            ${pushPerm !== 'granted' && pushPerm !== 'denied' && html`
              <button
                onClick=${async () => {
                  if (enabling) return;
                  setEnabling(true);
                  try {
                    const token = await requestPushPermission();
                    if (token) showToast('Notifications enabled', 1800);
                  } finally { setEnabling(false); }
                }}
                disabled=${enabling}
                style="padding:6px 14px;font-size:0.78rem;background:#25D366;color:#0a1f0e;font-weight:600;border:none;border-radius:8px;cursor:pointer;font-family:inherit"
              >
                ${enabling ? 'Enabling…' : 'Enable'}
              </button>
            `}
          </div>

          <!-- #181 — Quiet hours: enable + start/end pickers in one row.
               Inline editor (not a separate screen) since it's just two
               time inputs and a toggle. Wraps overnight (e.g. 22:00 → 08:00). -->
          <div style="border-top:1px solid rgba(255,255,255,0.06);padding:14px">
            <div style="display:flex;align-items:center;gap:12px">
              <div style="flex-shrink:0;width:36px;height:36px;border-radius:10px;background:rgba(96,165,250,0.14);display:flex;align-items:center;justify-content:center;color:#93c5fd">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              </div>
              <div style="flex:1;min-width:0">
                <div style="font-size:0.9rem;font-weight:600">Quiet hours</div>
                <div style="font-size:0.72rem;color:var(--text-muted,#7d8799);line-height:1.4;margin-top:2px">
                  Silences sound + in-app banners during this window. Push notifications still arrive — also DND your phone for hard silence.
                </div>
              </div>
              <div
                onClick=${() => {
                  const next = setQuietHours({ enabled: !quiet.enabled });
                  showToast(next.enabled ? 'Quiet hours on' : 'Quiet hours off', 1500);
                }}
                data-qr-id="quiet-hours-toggle"
                style="position:relative;width:46px;height:28px;flex-shrink:0;cursor:pointer;-webkit-tap-highlight-color:transparent"
                role="switch"
                aria-checked=${quiet.enabled}
                title=${quiet.enabled ? 'Tap to disable' : 'Tap to enable'}
              >
                <div style="position:absolute;inset:0;background:${quiet.enabled ? '#25D366' : 'rgba(255,255,255,0.12)'};border-radius:14px;transition:background 0.2s ease"></div>
                <div style="position:absolute;top:3px;left:${quiet.enabled ? '21px' : '3px'};width:22px;height:22px;background:#fff;border-radius:50%;box-shadow:0 1px 3px rgba(0,0,0,0.3);transition:left 0.2s ease"></div>
              </div>
            </div>
            ${quiet.enabled && html`
              <div style="display:flex;gap:10px;margin-top:10px;padding-left:48px">
                <label style="flex:1;display:flex;flex-direction:column;gap:4px">
                  <span style="font-size:0.7rem;color:var(--text-muted,#7d8799);text-transform:uppercase;letter-spacing:0.06em">Start</span>
                  <input
                    type="time"
                    data-qr-id="quiet-hours-start"
                    value=${_minToHHMM(quiet.startMin)}
                    onInput=${(e) => setQuietHours({ startMin: _hhmmToMin(e.target.value) })}
                    style="background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.1);border-radius:8px;padding:8px 10px;color:var(--text-primary);font-family:inherit;font-size:0.85rem;color-scheme:dark"
                  />
                </label>
                <label style="flex:1;display:flex;flex-direction:column;gap:4px">
                  <span style="font-size:0.7rem;color:var(--text-muted,#7d8799);text-transform:uppercase;letter-spacing:0.06em">End</span>
                  <input
                    type="time"
                    data-qr-id="quiet-hours-end"
                    value=${_minToHHMM(quiet.endMin)}
                    onInput=${(e) => setQuietHours({ endMin: _hhmmToMin(e.target.value) })}
                    style="background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.1);border-radius:8px;padding:8px 10px;color:var(--text-primary);font-family:inherit;font-size:0.85rem;color-scheme:dark"
                  />
                </label>
              </div>
            `}
          </div>

          <!-- #180 — Muted chats list. Empty state explains where to mute
               from (Contact info → Mute this chat). Not-empty shows each
               muted contact with a one-tap unmute. -->
          <div style="border-top:1px solid rgba(255,255,255,0.06);padding:14px">
            <div style="display:flex;align-items:center;gap:12px;margin-bottom:${mutedRows.length ? '10px' : '0'}">
              <div style="flex-shrink:0;width:36px;height:36px;border-radius:10px;background:rgba(245,158,11,0.14);display:flex;align-items:center;justify-content:center;color:#fbbf24">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="1" y1="1" x2="23" y2="23"/>
                  <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V5a3 3 0 0 0-5.94-.6"/>
                  <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/>
                  <line x1="12" y1="19" x2="12" y2="23"/>
                </svg>
              </div>
              <div style="flex:1;min-width:0">
                <div style="font-size:0.9rem;font-weight:600">Muted chats</div>
                <div style="font-size:0.72rem;color:var(--text-muted,#7d8799);line-height:1.4;margin-top:2px">
                  ${mutedRows.length
                    ? `${mutedRows.length} chat${mutedRows.length === 1 ? '' : 's'} silenced. Mute toggle is in each chat's Contact info → Actions.`
                    : 'No muted chats. Open a chat → Contact info → Mute this chat to silence specific customers.'}
                </div>
              </div>
            </div>
            ${mutedRows.length > 0 && html`
              <div style="padding-left:48px;display:flex;flex-direction:column;gap:6px">
                ${mutedRows.map(r => html`
                  <div
                    key=${r.contactId}
                    style="display:flex;align-items:center;gap:10px;padding:8px 10px;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.06);border-radius:8px"
                  >
                    <span style="flex:1;min-width:0;font-size:0.82rem;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${r.name}</span>
                    <button
                      onClick=${() => { unmuteChat(r.contactId); showToast('Chat unmuted', 1500); }}
                      class="inbox-btn-link"
                      style="font-size:0.78rem;color:#25D366;font-weight:600"
                      data-qr-id="unmute-row"
                    >Unmute</button>
                  </div>
                `)}
              </div>
            `}
          </div>
        <//>

        <${SectionHeader}>Account customization<//>
        <${Card}>
          <${SettingsRow}
            label="Channel colors + labels"
            sub="Owner-only — manage in the end-user dashboard under Account → Channels"
            onClick=${goDashboard}
            icon=${html`
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="13.5" cy="6.5" r=".5"/>
                <circle cx="17.5" cy="10.5" r=".5"/>
                <circle cx="8.5" cy="7.5" r=".5"/>
                <circle cx="6.5" cy="12.5" r=".5"/>
                <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/>
              </svg>
            `}
          />
        <//>

        <${SectionHeader}>Diagnostics<//>
        <${Card}>
          <!-- Inbox-wide diagnostic. Customer-friendly status badges
               at the top — no raw error text, no internal IDs visible.
               The Copy / Download buttons attach the full payload
               (account ids, permissions audit, push state, device info,
               last error, health probe results) for support to triage. -->
          <div style="padding:14px;display:flex;flex-direction:column;gap:12px">
            <div style="display:flex;align-items:center;gap:12px">
              <div style="flex-shrink:0;width:36px;height:36px;border-radius:10px;background:rgba(96,165,250,0.14);display:flex;align-items:center;justify-content:center;color:#93c5fd">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M9 11H1l8-8 8 8h-8v8z"/>
                  <path d="M22 12h-4l3 9H10l-3-9H3"/>
                </svg>
              </div>
              <div style="flex:1;min-width:0">
                <div style="font-size:0.9rem;font-weight:600">Inbox diagnostic</div>
                <div style="font-size:0.72rem;color:var(--text-muted,#7d8799);margin-top:1px;line-height:1.4">
                  Snapshot of account, device, push & connectivity. Copy or download to share with support if anything looks off.
                </div>
              </div>
              ${!diag && html`
                <button
                  onClick=${runDiag}
                  disabled=${diagBusy}
                  style="padding:6px 14px;font-size:0.78rem;background:#25D366;color:#0a1f0e;font-weight:600;border:none;border-radius:8px;cursor:pointer;font-family:inherit"
                >${diagBusy ? 'Running…' : 'Run'}</button>
              `}
            </div>

            ${diag && (() => {
              const rows = _diagStatusRows(diag);
              const anyIssue = rows.some(r => !r.ok);
              return html`
                <div style="background:${anyIssue ? 'rgba(245,158,11,0.06)' : 'rgba(37,211,102,0.04)'};border:1px solid ${anyIssue ? 'rgba(245,158,11,0.22)' : 'rgba(37,211,102,0.18)'};border-radius:10px;padding:10px 12px;display:flex;flex-direction:column;gap:6px">
                  ${rows.map(r => html`
                    <div style="display:flex;align-items:center;gap:10px;font-size:0.82rem">
                      <span style="flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;background:${r.ok ? 'rgba(37,211,102,0.18)' : 'rgba(245,158,11,0.2)'};color:${r.ok ? '#34d399' : '#fbbf24'}">
                        ${r.ok
                          ? html`<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`
                          : html`<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="9"  x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`}
                      </span>
                      <span style="flex:1;font-weight:600">${r.label}</span>
                      <span style="font-size:0.72rem;color:var(--text-muted,#7d8799);text-align:right">${r.hint}</span>
                    </div>
                  `)}
                </div>

                <div style="display:flex;gap:8px;flex-wrap:wrap">
                  <button
                    onClick=${copyDiag}
                    disabled=${diagBusy}
                    style="flex:1;min-width:120px;padding:9px 12px;font-size:0.78rem;background:#25D366;color:#0a1f0e;font-weight:600;border:none;border-radius:9px;cursor:pointer;font-family:inherit;display:flex;align-items:center;justify-content:center;gap:6px"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    Copy
                  </button>
                  <button
                    onClick=${downloadDiag}
                    disabled=${diagBusy}
                    style="flex:1;min-width:120px;padding:9px 12px;font-size:0.78rem;background:rgba(255,255,255,0.06);color:var(--v2-text);font-weight:600;border:1px solid rgba(255,255,255,0.12);border-radius:9px;cursor:pointer;font-family:inherit;display:flex;align-items:center;justify-content:center;gap:6px"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Download
                  </button>
                </div>

                <div style="display:flex;gap:8px;flex-wrap:wrap">
                  <button
                    onClick=${reRegisterPush}
                    disabled=${diagBusy}
                    style="flex:1;min-width:140px;padding:8px 12px;font-size:0.74rem;background:rgba(37,211,102,0.1);color:#34d399;font-weight:600;border:1px solid rgba(37,211,102,0.32);border-radius:8px;cursor:pointer;font-family:inherit"
                  >${diagBusy ? '…' : 'Re-register push'}</button>
                  <button
                    onClick=${runDiag}
                    disabled=${diagBusy}
                    style="flex:1;min-width:120px;padding:8px 12px;font-size:0.74rem;background:transparent;color:var(--text-muted,#7d8799);font-weight:500;border:1px solid rgba(255,255,255,0.08);border-radius:8px;cursor:pointer;font-family:inherit"
                  >${diagBusy ? '…' : 'Refresh'}</button>
                </div>

                <div style="font-size:0.66rem;color:var(--text-muted,#7d8799);text-align:center;line-height:1.4">
                  Captured ${diag.at.toLocaleTimeString()}.
                  ${diag.session.userType === 'owner' ? '' : 'Detailed info hidden — included in Copy / Download.'}
                </div>
              `;
            })()}
          </div>
        <//>

        <div style="padding:14px 14px 0;font-size:0.68rem;color:var(--text-muted, #7d8799);text-align:center;font-family:ui-monospace,SFMono-Regular,Menlo,monospace">
          ODIV Inbox · ${BUILD_VERSION}
        </div>
      </div>
    </div>
  `;
}
